import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Spinner,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardText, CheckCircle, WarningCircle, Clock, MagnifyingGlass } from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { useAuth } from '../../../../providers/auth';
import { pct } from '../format';
import {
  IAuditoriaEmpresa,
  IObrigacaoEmpresa,
  IObrigacaoCarteira,
  IObrigacoesEcac,
  IObrigacaoEcacRegistro,
} from '../types';
import { KpiCard } from '../components/KpiCard';
import { EmptyState } from '../components/EmptyState';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { UI } from '../ui';

interface ObrigacoesScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Drill: abre a empresa da carteira num submódulo (dashboard por padrão). */
  onOpenEmpresaScreen: (id: number, screen: string) => void;
}

type Aba = 'empresa' | 'ecac' | 'carteira';

const GLASS = {
  borderRadius: '16px',
  overflow: 'hidden',
  bg: 'rgba(255,255,255,0.7)',
  backdropFilter: 'blur(14px) saturate(160%)',
  border: '1px solid rgba(255,255,255,0.85)',
  boxShadow: '0 12px 34px -18px rgba(15,23,42,0.35)',
} as const;

const COLS_EMP = ['esocial', 'dctfweb', 'reinf', 'mit', 'das', 'fiscal', 'folha'] as const;
const COLS_CART = ['fiscal', 'folha', 'esocial', 'dctfweb', 'reinf', 'mit'] as const;
const isAtrasado = (v: string) => (v || '').toLowerCase() === 'atrasado';
const isPendente = (v: string) => (v || '').toLowerCase() === 'pendente';
const isEntregue = (v: string) => ['entregue', 'ok', 'calculado'].includes((v || '').toLowerCase());

// Obrigações que, no eCAC/GOB, NÃO emitem número de recibo (100% sem recibo nos
// dados, mesmo entregues). Excluídas do alerta "recibo faltante" p/ não gerar falso
// positivo — o alerta só faz sentido nas obrigações que normalmente têm recibo.
const OBRIG_SEM_RECIBO = new Set(['DCTF', 'ESOCIAL', 'ECD', 'DIRF']);
const emiteRecibo = (obrigacao: string) =>
  !OBRIG_SEM_RECIBO.has((obrigacao || '').trim().toUpperCase());

const corConforme = (p: number): string => {
  if (p >= 90) return UI.pos;
  if (p >= 60) return UI.warn;
  return UI.neg;
};

/** Cor do badge a partir do texto de status da obrigação. */
function statusStyle(value: string): { bg: string; color: string } {
  const v = (value || '').toLowerCase();
  if (['entregue', 'ok', 'calculado'].includes(v)) return { bg: UI.posSoft, color: UI.pos };
  if (['atrasado'].includes(v)) return { bg: UI.negSoft, color: UI.neg };
  if (['pendente'].includes(v)) return { bg: UI.warnSoft, color: UI.warn };
  return { bg: '#EFF2F6', color: UI.muted };
}

function ObrBadge({ value }: { value: string }) {
  if (!value) return <Text color={UI.muted}>—</Text>;
  const s = statusStyle(value);
  return (
    <Badge
      style={{ backgroundColor: s.bg, color: s.color }}
      borderRadius="full"
      px={2.5}
      py={0.5}
      fontSize="11px"
      fontWeight={700}
      textTransform="none"
    >
      {value}
    </Badge>
  );
}

/** Cor do badge do eCAC a partir do campo `badge` que já vem pronto do backend. */
const ECAC_BADGE: Record<IObrigacaoEcacRegistro['badge'], { bg: string; color: string }> = {
  green: { bg: UI.posSoft, color: UI.pos },
  red: { bg: UI.negSoft, color: UI.neg },
  amber: { bg: UI.warnSoft, color: UI.warn },
  gray: { bg: '#EFF2F6', color: UI.muted },
};

function EcacBadge({ registro }: { registro: IObrigacaoEcacRegistro }) {
  const s = ECAC_BADGE[registro.badge] ?? ECAC_BADGE.gray;
  return (
    <Badge
      style={{ backgroundColor: s.bg, color: s.color }}
      borderRadius="full"
      px={2.5}
      py={0.5}
      fontSize="11px"
      fontWeight={700}
      textTransform="none"
    >
      {/* `status` é o campo REAL de entrega (alinha com a cor do badge). O `situacao`
          vem furado do ETL GOB — "Não Entregue" mesmo p/ obrigações com transmissão+recibo. */}
      {registro.status || registro.situacao}
    </Badge>
  );
}

export function ObrigacoesScreen({ empresa, periodo, onOpenEmpresaScreen }: ObrigacoesScreenProps) {
  const { getObrigacoesEmpresa, getObrigacoesCarteira, getObrigacoesEcac } =
    useAuditoriaInteligente();
  const { temVisaoTodosGrupos } = useAuth();
  const visaoCarteira = temVisaoTodosGrupos();
  const [busca, setBusca] = useState('');

  const abaInicial: Aba = !empresa && visaoCarteira ? 'carteira' : 'empresa';
  const [aba, setAba] = useState<Aba>(abaInicial);

  const { data: empresaData, isFetching: loadingEmpresa } = useQuery<IObrigacaoEmpresa[]>({
    queryKey: ['ai-obrigacoes-empresa', { id: empresa?.id, ...periodo }],
    queryFn: getObrigacoesEmpresa,
    enabled: aba === 'empresa' && !!empresa,
  });

  const { data: ecacData, isFetching: loadingEcac } = useQuery<IObrigacoesEcac>({
    queryKey: ['ai-obrigacoes-ecac', { id: empresa?.id, ...periodo }],
    queryFn: getObrigacoesEcac,
    enabled: aba === 'ecac' && !!empresa,
  });

  const { data: carteiraData, isFetching: loadingCarteira } = useQuery<IObrigacaoCarteira[]>({
    queryKey: ['ai-obrigacoes-carteira', { ...periodo }],
    queryFn: getObrigacoesCarteira,
    enabled: aba === 'carteira' && visaoCarteira,
  });

  // Contagem de status (empresa atual): varre as 7 obrigações de cada competência.
  const statusEmpresa = useMemo(() => {
    let entregue = 0;
    let pendente = 0;
    let atrasado = 0;
    (empresaData ?? []).forEach(o => {
      COLS_EMP.forEach(k => {
        const v = (o as unknown as Record<string, string>)[k];
        if (isAtrasado(v)) atrasado += 1;
        else if (isPendente(v)) pendente += 1;
        else if (isEntregue(v)) entregue += 1;
      });
    });
    return { entregue, pendente, atrasado };
  }, [empresaData]);

  // Carteira: filtro de busca + % conforme por empresa.
  const carteiraView = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const todos = carteiraData ?? [];
    const filtrados = q
      ? todos.filter(o => `${o.nome} ${o.external_id}`.toLowerCase().includes(q))
      : todos;
    return filtrados.map(o => {
      const vals = COLS_CART.map(k => (o as unknown as Record<string, string>)[k]);
      const consideradas = vals.filter(v => isEntregue(v) || isAtrasado(v) || isPendente(v));
      const ok = vals.filter(isEntregue).length;
      const conforme = consideradas.length ? (ok / consideradas.length) * 100 : 0;
      return { o, conforme };
    });
  }, [carteiraData, busca]);

  const tabs: { value: Aba; label: string }[] = [
    { value: 'empresa', label: 'Empresa atual' },
    { value: 'ecac', label: 'eCAC (Receita)' },
    ...(visaoCarteira ? [{ value: 'carteira' as Aba, label: 'Todas as empresas' }] : []),
  ];

  return (
    <Box>
      <Flex gap={1} borderBottom="1px solid #E7ECF2" mb={5} wrap="wrap">
        {tabs.map(t => {
          const isActive = t.value === aba;
          return (
            <Flex
              key={t.value}
              align="center"
              px={4}
              py={3}
              cursor="pointer"
              onClick={() => setAba(t.value)}
              borderBottom="2.5px solid"
              borderColor={isActive ? UI.brand : 'transparent'}
              color={isActive ? UI.brand : UI.muted}
              mb="-1px"
              _hover={{ color: isActive ? UI.brand : UI.ink }}
            >
              <Text fontSize="13px" fontWeight={700}>
                {t.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      {(aba === 'empresa' || aba === 'ecac') && !empresa && (
        <EmptyState
          title="Selecione uma empresa"
          message={
            visaoCarteira
              ? 'Use a busca no topo (ID, CNPJ ou Razão Social) ou veja a aba “Todas as empresas”.'
              : 'Use a busca no topo (ID, CNPJ ou Razão Social) para ver as obrigações da empresa.'
          }
          icon={ClipboardText}
        />
      )}

      {aba === 'empresa' && empresa && (
        <Box>
          <SimpleGrid columns={{ base: 3 }} spacing={4} mb={5}>
            <KpiCard
              label="Entregues"
              value={statusEmpresa.entregue}
              icon={CheckCircle}
              iconColor={UI.pos}
              iconBg={UI.posSoft}
              isLoading={loadingEmpresa}
              info={
                <InfoBloco
                  oque="Total de obrigações da empresa entregues no prazo no período — somando todas as competências (eSocial, DCTFWeb, Reinf, MIT, DAS, Fiscal e Folha)."
                  dados="Derivado das tabelas extract_esocial_eventos, extract_dctfweb, extract_efd_reinf, extract_mit, extract_simples_nacional, extract_impostos e extract_resumo_folha (status Entregue/Calculado via data de envio x prazo legal dia 15/20)."
                  filtro={FILTRO.empresa}
                />
              }
            />
            <KpiCard
              label="Pendentes"
              value={statusEmpresa.pendente}
              icon={Clock}
              iconColor={UI.warn}
              iconBg={UI.warnSoft}
              isLoading={loadingEmpresa}
              info={
                <InfoBloco
                  oque="Obrigações ainda dentro do prazo e sem envio registrado no período — pendentes de entrega (sem data de envio e prazo legal ainda não vencido)."
                  dados="Mesmas fontes extract_esocial_eventos / extract_dctfweb / extract_efd_reinf / extract_mit / extract_impostos / extract_resumo_folha / extract_simples_nacional; status Pendente quando não há data de envio e o prazo (dia 15/20 do mês seguinte) ainda não passou."
                  filtro={FILTRO.empresa}
                />
              }
            />
            <KpiCard
              label="Atrasadas"
              value={statusEmpresa.atrasado}
              icon={WarningCircle}
              iconColor={UI.neg}
              iconBg={UI.negSoft}
              isLoading={loadingEmpresa}
              info={
                <InfoBloco
                  oque="Obrigações com o prazo legal já vencido e sem envio, ou enviadas depois do prazo — entregues em atraso no período."
                  dados="Mesmas fontes extract_esocial_eventos / extract_dctfweb / extract_efd_reinf / extract_mit (e demais extract_*); status Atrasado quando a data de envio é posterior ao prazo (dia 15/20 do mês seguinte) ou o prazo passou sem envio."
                  filtro={FILTRO.empresa}
                />
              }
            />
          </SimpleGrid>
          <Box {...GLASS}>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={UI.rowHover}>
                  <Tr>
                    <Th>Competência</Th>
                    <Th>eSocial</Th>
                    <Th>DCTFWeb</Th>
                    <Th>Reinf</Th>
                    <Th>MIT</Th>
                    <Th>DAS</Th>
                    <Th>Fiscal</Th>
                    <Th>Folha</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loadingEmpresa && (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={8}>
                        <Spinner />
                      </Td>
                    </Tr>
                  )}
                  {!loadingEmpresa &&
                    (empresaData ?? []).map(o => (
                      <Tr key={o.competencia} _hover={{ bg: UI.rowHover }}>
                        <Td fontWeight={700}>{o.competencia}</Td>
                        <Td>
                          <ObrBadge value={o.esocial} />
                        </Td>
                        <Td>
                          <ObrBadge value={o.dctfweb} />
                        </Td>
                        <Td>
                          <ObrBadge value={o.reinf} />
                        </Td>
                        <Td>
                          <ObrBadge value={o.mit} />
                        </Td>
                        <Td>
                          <ObrBadge value={o.das} />
                        </Td>
                        <Td>
                          <ObrBadge value={o.fiscal} />
                        </Td>
                        <Td>
                          <ObrBadge value={o.folha} />
                        </Td>
                      </Tr>
                    ))}
                  {!loadingEmpresa && (empresaData ?? []).length === 0 && (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={8} color={UI.muted}>
                        Sem obrigações no período.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      {aba === 'ecac' && empresa && (
        <>
          {loadingEcac && (
            <Box
              bg="white"
              border="1px solid"
              borderColor={UI.border}
              borderRadius="16px"
              py={8}
              textAlign="center"
            >
              <Spinner />
            </Box>
          )}
          {!loadingEcac && ecacData && !ecacData.fonte_disponivel && (
            <EmptyState
              title="Fonte não disponível"
              message="O registro do eCAC ainda não está disponível para esta empresa neste ambiente. Assim que estiver, os dados aparecem aqui."
              icon={ClipboardText}
            />
          )}
          {!loadingEcac && ecacData?.fonte_disponivel && (
            <Box {...GLASS}>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>Competência</Th>
                      <Th>Obrigação</Th>
                      <Th>Situação</Th>
                      <Th>Transmissão</Th>
                      <Th>Recibo</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(ecacData.registros ?? []).map(r => {
                      // Entregue (status real) + obrigação que normalmente tem recibo +
                      // sem recibo registrado = anomalia (sentinela; não marca DCTF/eSocial/ECD/DIRF).
                      const reciboFaltante =
                        isEntregue(r.status) && emiteRecibo(r.obrigacao) && !r.recibo;
                      return (
                        <Tr
                          key={`${r.competencia}-${r.obrigacao}-${r.recibo ?? ''}`}
                          _hover={{ bg: UI.rowHover }}
                          bg={reciboFaltante ? UI.warnSoft : undefined}
                        >
                          <Td fontWeight={700}>{r.competencia}</Td>
                          <Td fontWeight={700}>{r.obrigacao}</Td>
                          <Td>
                            <EcacBadge registro={r} />
                          </Td>
                          <Td color={r.data_transmissao ? UI.ink : UI.muted}>
                            {r.data_transmissao ?? '—'}
                          </Td>
                          <Td
                            color={reciboFaltante ? UI.warn : UI.muted}
                            fontWeight={reciboFaltante ? 700 : 400}
                          >
                            {r.recibo ?? (reciboFaltante ? '⚠ faltante' : '—')}
                          </Td>
                        </Tr>
                      );
                    })}
                    {(ecacData.registros ?? []).length === 0 && (
                      <Tr>
                        <Td colSpan={5} textAlign="center" py={8} color={UI.muted}>
                          Sem registros no eCAC para o período.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}

      {aba === 'carteira' && visaoCarteira && (
        <Box {...GLASS}>
          <Flex px={5} pt={4} pb={3} justify="space-between" align="center" gap={3} wrap="wrap">
            <Text fontSize="13px" fontWeight={700} color={UI.ink}>
              Obrigações por empresa{' '}
              <Text as="span" color={UI.faint} fontWeight={600}>
                ({carteiraView.length})
              </Text>
            </Text>
            <InputGroup size="sm" w={{ base: '100%', md: '260px' }}>
              <InputLeftElement pointerEvents="none" color={UI.muted}>
                <MagnifyingGlass size={15} />
              </InputLeftElement>
              <Input
                placeholder="Razão social ou ID…"
                value={busca}
                onChange={e => setBusca(e.target.value)}
                borderRadius="10px"
                bg="white"
              />
            </InputGroup>
          </Flex>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>ID</Th>
                  <Th>Razão Social</Th>
                  <Th isNumeric>% conforme</Th>
                  <Th>Fiscal</Th>
                  <Th>Folha</Th>
                  <Th>eSocial</Th>
                  <Th>DCTFWeb</Th>
                  <Th>Reinf</Th>
                  <Th>MIT</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loadingCarteira && (
                  <Tr>
                    <Td colSpan={9} textAlign="center" py={8}>
                      <Spinner />
                    </Td>
                  </Tr>
                )}
                {!loadingCarteira &&
                  carteiraView.map(({ o, conforme }) => (
                    <Tr
                      key={o.id}
                      cursor="pointer"
                      _hover={{ bg: UI.rowHover }}
                      onClick={() => onOpenEmpresaScreen(o.id, 'dashboard')}
                      title="Abrir o dashboard desta empresa"
                    >
                      <Td fontWeight={700} color={UI.brand}>
                        {o.external_id}
                      </Td>
                      <Td maxW="240px" whiteSpace="normal" fontWeight={700}>
                        {o.nome}
                      </Td>
                      <Td isNumeric fontWeight={800} color={corConforme(conforme)}>
                        {pct(conforme)}
                      </Td>
                      <Td>
                        <ObrBadge value={o.fiscal} />
                      </Td>
                      <Td>
                        <ObrBadge value={o.folha} />
                      </Td>
                      <Td>
                        <ObrBadge value={o.esocial} />
                      </Td>
                      <Td>
                        <ObrBadge value={o.dctfweb} />
                      </Td>
                      <Td>
                        <ObrBadge value={o.reinf} />
                      </Td>
                      <Td>
                        <ObrBadge value={o.mit} />
                      </Td>
                    </Tr>
                  ))}
                {!loadingCarteira && carteiraView.length === 0 && (
                  <Tr>
                    <Td colSpan={9} textAlign="center" py={8} color={UI.muted}>
                      {busca ? 'Nenhuma empresa com esse filtro.' : 'Sem dados no período.'}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
